import {useCallback, useEffect} from 'react';

export function removeCommentHighlight(editor, highlightId) {
  let result = false;

  editor.state.doc.descendants((node, pos) => {
    if (node.isText || node.isInline) {
      node.marks.forEach((mark) => {
        const dataId = mark.attrs.highlightId;

        if (dataId === highlightId) {
          const from = pos;
          const to = pos + node.nodeSize;

          // Create and dispatch a transaction to remove the mark
          const transaction = editor.state.tr.removeMark(from, to, mark.type);
          editor.view.dispatch(transaction);
          result = true;
        }
      });
    }
    // handle cases where the node is a table
    else if (node.type.name === 'table') {
      const parsed = findCellFromHighlightId(editor.state, highlightId);
      if (!parsed) return false;

      const {tablePos, row, col, node: tableNode, meta} = parsed;
      delete meta[`${row}-${col}`]?.backgroundColor;

      const tr = editor.state.tr.setNodeMarkup(tablePos, null, {
        ...tableNode.attrs,
        'table-meta': JSON.stringify(meta),
      });
      editor.view.dispatch(tr);
      result = true;
    }
    // handle cases where the Node itself is marked (linkedText)
    if (node.attrs.highlightId === highlightId) {
      const from = pos;
      const to = pos + node.nodeSize;

      const transaction = editor.state.tr.removeMark(
        from,
        to,
        editor.schema.marks.commentHighlight
      );
      editor.view.dispatch(transaction);
      result = true;
    }
  });

  return result;
}

// Reapply a comment highlight mark if a previously resolved comment is re-opened
export function reapplyCommentHighlight(editor, highlightId, metadata) {
  let result = false;
  let {from, to} = metadata;

  // Check if the 'from' position is still valid
  if (from < 0 || from > editor.state.doc.content.size) {
    console.warn('Invalid highlight start position');
    return false;
  }

  // Find the end of the current content starting from the 'from' position
  const $from = editor.state.doc.resolve(from);
  const $to = editor.state.doc.resolve(
    Math.min(to, $from.node().nodeSize + $from.start())
  );

  // Adjust 'to' to the end of the current content
  to = $to.pos;

  if (highlightId.startsWith('table-')) {
    editor.storage.commentHighlight.clickedHighlightId = highlightId;
    editor.commands.setMarkTableCell({highlightId});
    result = true;
  } else {
    const mark = editor.schema.marks.commentHighlight.create({
      highlightId,
      state: 'complete',
    });

    const transaction = editor.state.tr.addMark(from, to, mark);
    editor.view.dispatch(transaction);
    result = true;
  }

  return result;
}

export function getResolvedCommentHighlight(editor, metadata) {
  const {highlightId, from, to, highlightText} = metadata;
  if (!highlightText?.trim() || !editor?.state?.doc) return null;

  if (highlightId?.startsWith('table-')) {
    const parsed = findCellFromHighlightId(editor.state, highlightId);
    return parsed ? {pos: parsed.tablePos, match: 'exact', ...parsed} : null;
  }

  const doc = editor.state.doc;
  const docSize = doc.content.size;
  const search = highlightText.trim();

  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));

  if (safeFrom < safeTo) {
    try {
      if (doc.textBetween(safeFrom, safeTo, ' ').trim() === search) {
        return {from: safeFrom, to: safeTo, pos: safeFrom, match: 'exact'};
      }
    } catch {
      /* position may resolve inside a node boundary */
    }
  }

  return {from: safeFrom, to: safeTo, pos: safeFrom, match: 'changed'};
}

// Get the mark and node of a comment highlight from `data-highlight-id`
export function getCommentHighlight(editor, highlightId) {
  let result = null;

  // Iterate over each node in the document
  editor.state.doc.descendants((node, pos) => {
    // Check if the node itself has the mark
    if (node.isText || node.isInline) {
      node.marks.forEach((mark) => {
        const dataId = mark.attrs.highlightId;

        if (dataId === highlightId) {
          result = {mark, node, pos};
        }
      });
    } else if (node.type.name === 'table') {
      const parsed = findCellFromHighlightId(editor.state, highlightId);
      if (parsed)
        result = {mark: null, node: parsed.node, pos: parsed.tablePos};
    }

    // Additionally, handle cases where the node itself is marked (linkedText)
    if (node.attrs.highlightId === highlightId) {
      const mark = node.marks.find((m) => m.type.name === 'commentHighlight');
      if (mark) {
        result = {mark, node, pos};
      }
    }
  });

  return result;
}

export function getCommentHighlightContent(highlightId, editor) {
  // Check if this is a table cell highlight ID
  if (highlightId && highlightId.startsWith('table-')) {
    const parsed = findCellFromHighlightId(editor.state, highlightId);
    if (parsed) {
      const {row, col, node, meta} = parsed;
      const cellMeta = meta[`${row}-${col}`];
      if (!cellMeta?.backgroundColor) return null;
      const tableData = parseTableDataFromNode(node);
      const cellValue = tableData[row]?.[col];
      return cellValue || '(empty cell)';
    }
    return null;
  }

  // First, check if the highlight actually exists in the editor state
  const highlight = getCommentHighlight(editor, highlightId);
  if (!highlight) {
    // If no highlight exists in the editor state, return null (comment was resolved/removed)
    return null;
  }

  // Look for highlight elements across all editor containers for non-table content
  const elements = document.querySelectorAll(
    `[data-highlight-id="${highlightId}"]`
  );
  if (!elements || elements.length === 0) return null;

  for (const elem of elements) {
    const linkedTextElem = elem.querySelector('.linked-text');

    if (linkedTextElem) {
      const content = linkedTextElem.innerHTML
        .replace(/&nbsp;|&ensp;|&emsp;|&thinsp;|&hairsp;/g, ' ')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .trim();
      if (content.length > 0) {
        return linkedTextElem.innerHTML;
      }
    }

    const content = elem.innerHTML
      .replace(/&nbsp;|&ensp;|&emsp;|&thinsp;|&hairsp;/g, ' ')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .trim();
    if (content.length > 0) {
      return elem.innerHTML;
    }
  }

  return null;
}

export const HIGHLIGHT_EVENT_NAME = 'commentHighlight';

// Trigger highlight active
export function highlightEvent(highlightId, isClick = false) {
  const event = new CustomEvent(HIGHLIGHT_EVENT_NAME, {
    detail: {highlightId, isClick},
  });

  document.documentElement.dispatchEvent(event);
}

export function useHighlightEvent() {
  return useCallback((highlightId) => {
    const event = new CustomEvent(HIGHLIGHT_EVENT_NAME, {
      detail: {highlightId},
    });

    document.documentElement.dispatchEvent(event);
  }, []);
}

export function useHighlightEventListener(callback) {
  useEffect(() => {
    function handler(event) {
      callback(event.detail.highlightId, event.detail.isClick);
    }

    document.documentElement.addEventListener(HIGHLIGHT_EVENT_NAME, handler);

    return () => {
      document.documentElement.removeEventListener(
        HIGHLIGHT_EVENT_NAME,
        handler
      );
    };
  }, [callback]);
}

export function getUserIdsFromThreads(threads) {
  const userIds = new Set();

  const extractMentionIds = (content) => {
    if (Array.isArray(content)) {
      content.forEach((item) => {
        if (item) {
          if (item.type === 'mention' && item.id) {
            userIds.add(item.id);
          }
          if (item.children) {
            extractMentionIds(item.children);
          }
        }
      });
    }
  };

  for (const thread of threads) {
    if (thread && thread.comments && Array.isArray(thread.comments)) {
      for (const comment of thread.comments) {
        if (comment) {
          if (comment.userId) {
            userIds.add(comment.userId);
          }

          if (comment.body && comment.body.content) {
            extractMentionIds(comment.body.content);
          }
        }
      }
    }
  }

  return Array.from(userIds);
}

export function threadMatchesFilters(thread, filters) {
  const {status, userId} = filters;

  const isResolved = thread.resolved || thread.metadata?.resolved;
  if (status === 'unresolved' && isResolved) return false;
  if (status === 'resolved' && !isResolved) return false;

  if (!userId) return true;

  const isAuthor = thread.comments.some((comment) => comment.userId === userId);
  if (isAuthor) return true;

  const isMentioned = thread.comments.some((comment) => {
    if (comment.body && comment.body.content) {
      return comment.body.content.some((content) => {
        if (Array.isArray(content.children)) {
          return content.children.some(
            (child) => child.type === 'mention' && child.id === userId
          );
        }
        return false;
      });
    }
    return false;
  });

  return isMentioned;
}

export function parseCellHighlightId(highlightId) {
  const match = highlightId?.match(/^table-([a-f0-9-]+)-row-(\d+)-col-(\d+)$/);
  if (match) {
    return {tableId: match[1], row: Number(match[2]), col: Number(match[3])};
  }
  return null;
}

export function findCellFromHighlightId(state, highlightId) {
  // Guard against missing parameters or incorrect types
  if (!state || !state.doc || typeof highlightId !== 'string') return null;

  const parsed = parseCellHighlightId(highlightId);
  if (!parsed) return null;

  const {tableId, row, col} = parsed;

  let tablePos = null;
  let tableNode = null;

  try {
    state.doc.descendants((node, pos) => {
      if (node.type.name === 'table' && node.attrs?.id === tableId) {
        tablePos = pos;
        tableNode = node;
        return false; // stop traversal once the table is found
      }
      return true;
    });
  } catch {
    return null;
  }

  if (tablePos == null || !tableNode) return null;

  // Attempt to parse the table-meta attribute; fall back to empty object on failure
  let meta = {};
  try {
    meta = JSON.parse(tableNode.attrs?.['table-meta'] || '{}');
  } catch {
    meta = {};
  }

  return {tablePos, row, col, node: tableNode, meta};
}

export function getCellText(state, highlightId) {
  let highlightText = '';
  const parsed = findCellFromHighlightId(state, highlightId);
  if (!parsed) return false;

  const {row, col, node} = parsed;
  const tableData = parseTableDataFromNode(node);
  const cellValue = tableData[row]?.[col];
  highlightText =
    cellValue != null && cellValue !== ''
      ? String(cellValue).trim()
      : '(empty cell)';

  return highlightText;
}

export function parseTableDataFromNode(node) {
  const tableDataString =
    node.attrs['table-data']?.replace(/\\'/g, "'") || '[]';
  try {
    const tableData = JSON.parse(tableDataString);
    return tableData;
  } catch (e) {
    console.warn('Failed to parse table-data while creating comment thread', e);
    return [];
  }
}
